import { PortalAirportNode, PortalOrderNode } from '@app/services/api/api.types';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function tripDestinationTitle(order: PortalOrderNode | null | undefined) {
  return order?.flightItineraries?.[0]?.arrivalAirport?.cityName ?? '';
}

export function tripCityImage(order: PortalOrderNode | null | undefined) {
  const city = order?.flightItineraries?.[0]?.arrivalAirport?.cityName?.trim().toLowerCase();
  return city ? `/assets/cities/${city}.webp` : '/assets/images/city.svg';
}

export function tripRouteLabel(order: PortalOrderNode | null | undefined) {
  const leg = order?.flightItineraries?.[0];
  const stops = [leg?.departureAirport, leg?.arrivalAirport].filter(
    (airport): airport is PortalAirportNode => Boolean(airport?.cityName),
  );
  const unique = stops.filter((airport, index) => {
    const previous = stops[index - 1];
    return !previous || previous.locationCode !== airport.locationCode;
  });

  if (!unique.length) {
    return '';
  }

  return `From ${unique.map(airportLabel).join(', ')}`;
}

export function tripDateLabel(order: PortalOrderNode | null | undefined) {
  const depart = formatTripDate(order?.flightItineraries?.[0]?.departureAt);
  const returning = formatTripDate(order?.roundTripDepartureAt);

  if (depart && returning && returning !== depart) {
    return `${depart} – ${returning}`;
  }

  return depart;
}

export function tripTravelerLabel(order: PortalOrderNode | null | undefined) {
  const counted =
    (order?.numberOfAdults ?? 0) + (order?.numberOfChildren ?? 0) + (order?.numberOfInfants ?? 0);
  const count = counted || order?.totalPassengers;

  if (!count) {
    return '';
  }

  return `${count} traveler${count === 1 ? '' : 's'}`;
}

function airportLabel(airport: PortalAirportNode) {
  return airport.locationCode ? `${airport.cityName} (${airport.locationCode})` : airport.cityName;
}

// Authorized dates are calendar days (`YYYY-MM-DD`). DatePipe parses them as UTC
// midnight and can show the previous day in timezones behind UTC.
function formatTripDate(value?: string | null) {
  if (!value) {
    return '';
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

  if (!match) {
    return '';
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return '';
  }

  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];

  return `${weekday}, ${MONTHS[month - 1]} ${day}`;
}
